/**
 * Which recruiter's board is on screen, and the roster to switch between them.
 *
 * Extracted from TAActionBoardView (at the repo's 500-line ceiling) because the roster fetch and the
 * selected-board state are one concern: the selection is only meaningful against the roster, and the
 * roster is what re-homes a stale selection.
 *
 * The selection is remembered across a hard refresh (sessionStorage, see boardSelection.ts): a reload
 * lands back on the board you were viewing rather than snapping to your own, while a fresh session
 * still opens on your own board.
 */
import { useCallback, useEffect, useState } from 'react';
import * as api from '../../services/taActionBoardApi';
import type { Recruiter } from '../../types/taActionBoard';
import { readSelectedBoard, writeSelectedBoard } from './boardSelection';

export interface BoardSelection {
  recruiters: Recruiter[];
  /** Email of the board currently shown. */
  selectedEmail: string;
  /** Switch boards and remember it for the next reload. */
  selectBoard: (email: string) => void;
}

export function useBoardSelection(myEmail: string): BoardSelection {
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);
  // Seed from the board last viewed this session so a hard refresh returns there; own board otherwise.
  const [selectedEmail, setSelectedEmail] = useState(() => readSelectedBoard(myEmail) || myEmail);

  useEffect(() => {
    api
      .fetchRecruiters()
      .then((list) => {
        setRecruiters(list);
        // Re-home only an INVALID selection (a stale stored board, or a non-recruiter viewer whose
        // own email isn't on the roster): prefer their own board, else the first entry. A valid
        // selection — restored from storage or their own — is left untouched.
        if (list.length > 0) {
          setSelectedEmail((current) => {
            if (list.some((r) => r.email === current)) return current;
            if (list.some((r) => r.email === myEmail)) return myEmail;
            return list[0].email;
          });
        }
      })
      .catch(() => setRecruiters([]));
  }, [myEmail]);

  const selectBoard = useCallback(
    (email: string) => {
      setSelectedEmail(email);
      writeSelectedBoard(myEmail, email); // so a hard refresh returns to this board
    },
    [myEmail]
  );

  return { recruiters, selectedEmail, selectBoard };
}
